import { FCWithChildren } from '@types';
import React, { Fragment, useEffect, useState } from 'react';

import classNames from 'classnames';
import { useToasts } from 'react-toast-notifications';

import { Button, Input, Label, SeparatorLine, HelperText, InputSectionGroup } from '@components';
import useLayoutContext from '@hooks/useLayoutContext';
import { useAppContext } from '@contexts/AppContext';
import useUpdateProfile from '@hooks/useUpdateProfile';
import { parseError } from '@utils';
import { TiWiFi } from 'react-icons/ti';

type SetUpPasswordProps = {};

type ErrorType = {
  status: string;
  data: {
    error?: string;
    errors?: Record<string, string[]>;
  };
};

const SetUpPassword: FCWithChildren<SetUpPasswordProps> = () => {
  const { user } = useAppContext();
  const { addToast } = useToasts();
  const { isMobile } = useLayoutContext();

  const [wifiPassword, setWifiPassword] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setWifiPassword(user?.preWiredKey);
  }, [user]);

  const handleSuccess = () =>
    addToast('Wi-Fi password saved  successfully', { appearance: 'success' });

  const { update, isPending } = useUpdateProfile({ id: user?.id, handleSuccess });

  const hasPasswordChanged = wifiPassword !== (user?.preWiredKey || '');

  const onFocusHandler = () => setIsFocused(true);
  const onBlurHandler = () => setIsFocused(false);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    isError && setIsError(false);
    setWifiPassword(e.target.value.trim());
  };

  const onClickHandler = () => {
    const preWiredKey = wifiPassword.trim();
    if ((preWiredKey || '').length < 8) {
      setIsError(true);
    } else {
      update(
        { preWiredKey },
        {
          onError: (apiError: ErrorType) => {
            if (apiError?.data?.error) {
              addToast(parseError(apiError?.data?.error), {
                appearance: 'error',
              });
            } else {
              addToast('Unknown error changing Wi-Fi Password', {
                appearance: 'error',
              });
            }
          },
        },
      );
    }
  };

  const containerCls = classNames(
    'flex flex-col justify-start my-2 ',
    isMobile ? 'w-full' : 'w-full md:pr-8 md:w-1/2 md:my-8',
  );

  const boxCls = classNames(
    'w-full bg-white md:p-4',
    isMobile ? 'w-full' : 'md:border md:border-gray-200 md:rounded md:shadow-xl',
  );

  const containerStyle = classNames(
    'flex items-center bg-white flex-wrap',
    isMobile ? 'flex-col justify-center' : null,
    // isFocused ? 'border-blue-400' : 'border-transparent border-gray-300'
  );

  return (
    <div className="w-full my-4 min-w-[230px]">
      <Label className="mb-4" htmlFor="password">
        <h2 className="flex gap-2 items-center text-black">
          <TiWiFi size="25" /> Wi-Fi Password
        </h2>
      </Label>
      <div className={containerStyle}>
        <InputSectionGroup className="w-full">
          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <div className="input-section-group__control">
                <Input
                  className="input-section-group__inline-input"
                  id="password"
                  name="wifiPassword"
                  onBlur={onBlurHandler}
                  onChange={onChangeHandler}
                  onFocus={onFocusHandler}
                  type="text"
                  value={wifiPassword}
                />
              </div>
            </div>
          </InputSectionGroup.Item>
        </InputSectionGroup>
        <div className="flex w-full gap-2 py-2">
          <Button
            className="flex"
            layout="outline"
            onClick={() => {
              navigator.clipboard.writeText(user.preWiredKey);
              addToast('Wi-Fi password copied', { appearance: 'success' });
            }}
          >
            Copy
          </Button>
          <Button
            className="flex-1"
            disabled={isPending || !hasPasswordChanged}
            loading={isPending}
            onClick={onClickHandler}
          >
            Save Password
          </Button>
        </div>
      </div>
      {isError && (
        <div className="flex items-center w-full h-8">
          <HelperText valid={false}>Password too short. Must be 8 characters long.</HelperText>
        </div>
      )}
    </div>
  );
};

export default SetUpPassword;
